import React, { useEffect } from 'react';
import { ScrollView } from 'react-native';
import { Box, Button, FormControl, HStack, IconButton, Input, Pressable, Text, VStack, useColorModeValue, useToast } from "native-base";
import * as JobAction from '../../redux/actions/jobAction';
import * as UserAction from '../../redux/actions/userAction';

import { useSelector, useDispatch } from "react-redux";
import { IStore } from '../../redux/reducers';
import { inputStyle } from 'src/styles/inputStyle';
import { containerStyle } from 'src/styles/containerStyle';
import Collapsible from 'react-native-collapsible';
import { InfoField } from 'src/models';
import { textStyle } from 'src/styles/textStyle';
import MaterialIconByName from '../MaterialIconByName';
import { getMappedFieldName } from 'src/utils/surveyorUtils';
import JobInfoImageUploadComponent from '../JobInfoImageUploadComponent';
import {getText} from 'src/utils/i18n';
import { isElectron } from 'src/utils/miscUtils';

export default function MeasurementInfo() {
    type AutoMeasuredFieldName = 'moisture' | 'sampleWeight';

    const draftJobId = useSelector((store: IStore) => store.job.draftJobId);
    const draftInfo = useSelector((store: IStore) => store.job.draftInfo);
    const draftLocked = useSelector((store: IStore) => store.job.draftLocked);

    const center = useSelector((store: IStore) => store.user?.userProfile?.response?.center);

    const surveyor = useSelector((store: IStore) => store.user?.userProfile?.response?.surveyor);

    const userSettings = useSelector((store: IStore) => store.user?.userProfile?.response?.settings);

    // New selectors for fetching info fields from API
    const username = useSelector((store: IStore) => store.user?.userProfile?.response?.username);
    const orgId = useSelector((store: IStore) => store.user?.userProfile?.response?.org?.orgId);
    const grainId = useSelector((store: IStore) => store.grainType.grainId);
    const profileId = useSelector((store: IStore) => store.grainProfile.profileId);
    const infoFieldsKey = `${orgId || ''}:${grainId || ''}:${profileId || ''}:info_fields`;
    const fetchedInfoFields = useSelector((store: IStore) => store.user?.userInfoFieldsByKey?.[infoFieldsKey]?.response);
    const isLoadingInfoFields = useSelector((store: IStore) => store.user?.userInfoFieldsByKey?.[infoFieldsKey]?.isFetching);
    const userInfoFields =
        fetchedInfoFields && fetchedInfoFields.length > 0
            ? fetchedInfoFields
            : userSettings?.info_fields;

    const dispatch = useDispatch();

    const locale = useSelector((state: IStore) => state.app.locale);
    const textColor = useColorModeValue('coolGray.800', 'coolGray.100');
    const toast = useToast();

    const [categoryFormFields, setCategoryFormFields] = React.useState<{ [key: string]: InfoField[] }>({});
    const [categoryExpanded, setCategoryExpanded] = React.useState<{ [key: string]: boolean }>({});
    const [measuringField, setMeasuringField] = React.useState<string | null>(null);
    const [deviceMeasuredFields, setDeviceMeasuredFields] = React.useState<Record<AutoMeasuredFieldName, boolean>>({
        moisture: false,
        sampleWeight: false
    });

    // Mapping of category keys to display labels
    const categoryLabels: { [key: string]: string } = {
        measurement: 'measurementInfo',
        seller: 'sellerInfo',
        surveyor: 'surveyorInfo',
        other_readings: 'otherReadings'
    };

    useEffect(() => {
        // Fetch info fields from API when component mounts or when required parameters change
        if (username && orgId && grainId && profileId) {
            dispatch(UserAction.getUserInfoFields.request({
                username: username,
                orgId: orgId,
                grainId: grainId,
                profileId: profileId,
                settingName: 'info_fields'
            }));
        }
    }, [username, orgId, grainId, profileId, dispatch]);

    useEffect(() => {
        // Process the fetched info fields and group them by category dynamically
        if (userInfoFields !== null && userInfoFields !== undefined) {
            const grouped: { [key: string]: InfoField[] } = {};
            const expanded: { [key: string]: boolean } = {};

            userInfoFields.forEach((item) => {
                if (!grouped[item.category]) {
                    grouped[item.category] = [];
                    expanded[item.category] = false;
                }
                grouped[item.category].push(item);
            });

            setCategoryFormFields(grouped);
            setCategoryExpanded(expanded);
        }
    }, [userInfoFields]);

    useEffect(() => {
        setDeviceMeasuredFields({ moisture: false, sampleWeight: false });
    }, [draftJobId]);

    useEffect(() => {
        if (!draftInfo || Object.keys(draftInfo).length === 0) {
            setDeviceMeasuredFields({ moisture: false, sampleWeight: false });
        }
    }, [draftInfo]);

    const handleCategoryPress = (category: string) => {
        setCategoryExpanded((prev) => ({
            ...(prev || {}),
            [category]: !prev?.[category]
        }));
    }

    const updateDraftInfoField = (fieldName: string, value: any) => {
        dispatch(JobAction.setDraftInfoField({
            fieldName,
            value: value === null || value === undefined ? '' : String(value)
        }));
    };

    const getDraftFieldValue = (fieldName: string) => {
        const value = draftInfo?.[fieldName];
        if (value === null || value === undefined) return '';
        return String(value);
    };

    const formatPortsForToast = (ports: any[] | undefined) => {
        if (!ports || ports.length === 0) return 'No ports detected';
        const summary = ports.slice(0, 6).map((p) => {
            const device = p?.device || 'unknown';
            const vid = p?.vid !== undefined && p?.vid !== null ? `vid:${p.vid}` : '';
            const pid = p?.pid !== undefined && p?.pid !== null ? `pid:${p.pid}` : '';
            const desc = p?.description ? String(p.description) : '';
            return [device, vid, pid, desc].filter(Boolean).join(' ');
        }).join(' | ');
        return ports.length > 6 ? `${summary} | …(+${ports.length - 6})` : summary;
    }

    const measureField = async (category: string, fieldName: AutoMeasuredFieldName) => {
        if (!isElectron() || typeof window === 'undefined' || !(window as any).electron) {
            toast.show({
                title: 'Measure unavailable',
                description: 'This feature is available only in the desktop app.',
                duration: 4000,
            });
            return;
        }

        const electronApi = (window as any).electron;
        const fn = fieldName === 'moisture' ? electronApi?.readMoisture : electronApi?.readScaleWeight;
        if (typeof fn !== 'function') {
            toast.show({
                title: 'Measure unavailable',
                description: 'Desktop integration API not available.',
                duration: 4000,
            });
            return;
        }

        setMeasuringField(fieldName);
        try {
            const res = await fn();
            if (res?.ok === true && typeof res?.value === 'number' && Number.isFinite(res.value)) {
                setDeviceMeasuredFields((prev) => ({ ...prev, [fieldName]: true }));
                updateDraftInfoField(fieldName, String(res.value));
                return;
            }

            toast.show({
                title: 'Measurement failed',
                description: `${res?.error || 'Unknown error'}. Detected ports: ${formatPortsForToast(res?.ports)}`,
                duration: 8000,
            });
        } catch (error) {
            toast.show({
                title: 'Measurement failed',
                description: `${error instanceof Error ? error.message : String(error)}`,
                duration: 8000,
            });
        } finally {
            setMeasuringField(null);
        }
    }

    // New Reading / Reset actions are pinned in InfoPage.

    return (<ScrollView>
        <Box
            key="measurementInfoContainer"
            style={containerStyle.mainContainer}>
            <Box
                key={"measurementInfo"}
                style={{
                    margin: 4
                }}>
                {
                    Object.keys(categoryFormFields).length > 0 && (categoryFormFields['measurement']?.length > 0) &&
                    <>
                        <Pressable
                            key={"measurementInfoHeaderPressable"}
                            onPress={() => {
                                handleCategoryPress('measurement')
                            }}
                            rounded="8"
                            overflow="hidden">
                            <HStack
                                key={"measurementInfoHeader"}
                                style={{
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                <Text
                                    key={"measurementInfoHeaderText"}
                                    color={textColor}
                                    style={textStyle.measurementSectionText}>
                                        {getText('measurementInfo')}
                                    </Text>
                                <IconButton
                                    variant="unstyled"
                                    _focus={{
                                        borderWidth: 0
                                    }}
                                    onPress={() => {
                                        handleCategoryPress('measurement')
                                    }}
                                    icon={<MaterialIconByName name={categoryExpanded['measurement'] ? "chevron-up" : "chevron-down"} />} />
                            </HStack>
                        </Pressable>
                        <Collapsible collapsed={!categoryExpanded['measurement']}>
                            <VStack>
                                <FormControl
                                    key={"measurementInfoFormControl"}
                                >
                                    {
                                        center &&
                                        center.centerId &&
                                        <>
                                            <FormControl.Label _text={textStyle.inputLabel}>{getText('centerId')}</FormControl.Label>
                                            <Input
                                                key={"centerId"}
                                                style={inputStyle.measurementInput}
                                                placeholder="Center / Warehouse ID"
                                                isReadOnly={true}
                                                value={center.centerId} />
                                        </>
                                    }
                                    <FormControl.Label _text={textStyle.inputLabel}>{getText('centerName')}</FormControl.Label>
                                    <Input
                                        key={"centerName"}
                                        style={inputStyle.measurementInput}
                                        placeholder="Center / Warehouse name"
                                        isReadOnly={true}
                                        value={center?.name} />
                                    {
                                        center &&
                                        center.addressDistrict &&
                                        center.addressDistrict !== "" &&
                                        <>
                                            <FormControl.Label _text={textStyle.inputLabel}>{getText('centerDistrict')}</FormControl.Label>
                                            <Input
                                                key={"warehouseDistrict"}
                                                style={inputStyle.measurementInput}
                                                placeholder="District"
                                                isReadOnly={true}
                                                value={center.addressDistrict} />
                                        </>
                                    }
                                    {
                                        center &&
                                        center.addressCity &&
                                        center.addressCity !== "" &&
                                        <>
                                            <FormControl.Label _text={textStyle.inputLabel}>{getText('centerCity')}</FormControl.Label>
                                            <Input
                                                key={"warehouseCity"}
                                                style={inputStyle.measurementInput}
                                                placeholder="City"
                                                isReadOnly={true}
                                                value={center.addressCity} />
                                        </>
                                    }
                                    {
                                        categoryFormFields['measurement']?.map((item: InfoField, index: number) => {
                                            const autoMeasuredFieldName: AutoMeasuredFieldName | null =
                                                item.fieldName === 'moisture' || item.fieldName === 'sampleWeight'
                                                    ? (item.fieldName as AutoMeasuredFieldName)
                                                    : null;
                                            const isAutoMeasuredField = isElectron() && autoMeasuredFieldName !== null;
                                            const isLockedAfterDeviceMeasure = Boolean(
                                                autoMeasuredFieldName && deviceMeasuredFields[autoMeasuredFieldName]
                                            );
                                            return (
                                                <VStack key={`measurement-${item.fieldName}-${index}`}>
                                                    {
                                                        item.type === "imageUpload" ?
                                                            <JobInfoImageUploadComponent
                                                                jobId={draftJobId}
                                                                displayName={item.displayName}
                                                                fieldName={item.fieldName}
                                                                isDisabled={draftLocked}
                                                                onSuccess={(s3Path) => {
                                                                    updateDraftInfoField(item.fieldName, s3Path);
                                                                }} />
                                                            : <>
                                                                <FormControl.Label _text={textStyle.inputLabel}>{item.displayName}</FormControl.Label>
                                                                <Input
                                                                    key={item.fieldName}
                                                                    value={getDraftFieldValue(item.fieldName)}
                                                                    style={inputStyle.measurementInput}
                                                                    placeholder={item.displayName}
                                                                    inputMode={item.type === "number" ? "decimal" : "text"}
                                                                    isReadOnly={draftLocked || (isAutoMeasuredField && isLockedAfterDeviceMeasure)}
                                                                    InputRightElement={
                                                                        isAutoMeasuredField ? (
                                                                            <Button
                                                                                size="xs"
                                                                                variant="outline"
                                                                                mr="2"
                                                                                isLoading={measuringField === item.fieldName}
                                                                                isDisabled={draftLocked || (measuringField !== null && measuringField !== item.fieldName)}
                                                                                onPress={() => measureField('measurement', autoMeasuredFieldName as AutoMeasuredFieldName)}
                                                                            >
                                                                                Measure
                                                                            </Button>
                                                                        ) : undefined
                                                                    }
                                                                    onChangeText={(value) => {
                                                                        if (draftLocked || (isAutoMeasuredField && isLockedAfterDeviceMeasure)) return;
                                                                        updateDraftInfoField(item.fieldName, value);
                                                                    }} />
                                                            </>
                                                    }
                                                </VStack>
                                            )
                                        })
                                    }

                                </FormControl>
                            </VStack>
                        </Collapsible>
                    </>
                }

                {
                    Object.keys(categoryFormFields).map((category: string) => {
                        if (category === 'measurement') return null;
                        
                        const fields = categoryFormFields[category];
                        if (!fields || fields.length === 0) return null;

                        const categoryLabel = categoryLabels[category] || category;

                        return (
                            <VStack key={`${category}-container`}>
                                <Pressable
                                    onPress={() => {
                                        handleCategoryPress(category)
                                    }}
                                    key={`${category}-HeaderPressable`}
                                    rounded="8"
                                    overflow="hidden">
                                    <HStack
                                        key={`${category}-Header`}
                                        style={{
                                            flexDirection: 'row',
                                            justifyContent: 'space-between',
                                            alignItems: 'center'
                                        }}>
                                        <Text color={textColor} style={textStyle.measurementSectionText}>{getText(categoryLabel)}</Text>
                                        <IconButton
                                            variant="unstyled"
                                            _focus={{
                                                borderWidth: 0
                                            }}
                                            onPress={() => {
                                                handleCategoryPress(category)
                                            }}
                                            icon={<MaterialIconByName name={categoryExpanded[category] ? "chevron-up" : "chevron-down"} />} />
                                    </HStack>
                                </Pressable>

                                <Collapsible
                                    key={`${category}-Collapsible`}
                                    collapsed={!categoryExpanded[category]}>
                                    <VStack>
                                        <FormControl
                                            key={`${category}-FormControl`}
                                        >
                                            {
                                                fields.map((item: InfoField, index: number) => {
                                                    return (
                                                        <VStack key={`${category}-${item.fieldName}-${index}`}>
                                                            {
                                                                item.type === "imageUpload" ?
                                                                    <JobInfoImageUploadComponent
                                                                        jobId={draftJobId}
                                                                        displayName={item.displayName}
                                                                        fieldName={item.fieldName}
                                                                        isDisabled={draftLocked}
                                                                        onSuccess={(s3Path) => {
                                                                            updateDraftInfoField(item.fieldName, s3Path);
                                                                        }} />
                                                                    : <>
                                                                        <FormControl.Label _text={textStyle.inputLabel}>{item.displayName}</FormControl.Label>
                                                                        <Input
                                                                            key={item.fieldName}
                                                                            value={getDraftFieldValue(item.fieldName)}
                                                                            style={inputStyle.measurementInput}
                                                                            placeholder={item.displayName}
                                                                            inputMode={item.type === "number" ? "decimal" : "text"}
                                                                            isReadOnly={draftLocked}
                                                                            InputRightElement={
                                                                                isElectron() && (item.fieldName === 'moisture' || item.fieldName === 'sampleWeight') ? (
                                                                                    <Button
                                                                                        size="xs"
                                                                                        variant="outline"
                                                                                        mr="2"
                                                                                        isLoading={measuringField === item.fieldName}
                                                                                        isDisabled={draftLocked || (measuringField !== null && measuringField !== item.fieldName)}
                                                                                        onPress={() => measureField(category, item.fieldName as 'moisture' | 'sampleWeight')}
                                                                                    >
                                                                                        Measure
                                                                                    </Button>
                                                                                ) : undefined
                                                                            }
                                                                            onChangeText={value => updateDraftInfoField(item.fieldName, value)} />
                                                                    </>
                                                            }
                                                        </VStack>
                                                    )
                                                })
                                            }

                                        </FormControl>
                                    </VStack>

                                </Collapsible>
                            </VStack>
                        );
                    })
                }

            </Box>


        </Box>
    </ScrollView>
    );
}
